import React, { createContext, useContext, useMemo, useState } from 'react';

/**
 * Open/close state for the manage-translations experience.
 *
 * The manage-translations view lives alongside a product's main config panel and is
 * shown/hidden by toggling a single boolean. Previously each config panel (carousel,
 * spotlight, page-topper, jsm-help) held its own `useState` for this, and any control
 * that opened the view had to be a direct child of the component that owned the state.
 * That made it impossible for deeply-nested controls (e.g. the "Manage translations"
 * button inside jsm-help's heading field) to open the view without prop-drilling the
 * setter through every layer.
 *
 * This context centralises that boolean so both the config panel body and any nested
 * trigger can read and update it from anywhere within the provider.
 */
export type ManageTranslationsOpenContextValue = {
	/**
	 * The identifier of the item (e.g. a carousel card) whose translations are being
	 * managed, or `null` when the view is scoped to the whole extension (all items).
	 *
	 * When set, consumers such as {@link ManageTranslations} should only render the
	 * translatable fields belonging to this item so that opening "Manage translations"
	 * on one card does not surface the fields of every other card.
	 */
	activeItemId: string | null;
	/** Closes the manage-translations view and clears any active item scope. */
	close: () => void;
	/** Whether the manage-translations view is currently open. */
	isOpen: boolean;
	/**
	 * Opens the manage-translations view.
	 *
	 * @param itemId Optional identifier of the item to scope the view to. When omitted
	 * the view shows the fields for the whole extension (previous behaviour).
	 */
	open: (itemId?: string) => void;
	/** Sets the open state directly (useful for `onOpenChange` handlers). */
	setIsOpen: (isOpen: boolean) => void;
};

const ManageTranslationsOpenContext = createContext<ManageTranslationsOpenContextValue | null>(
	null,
);

export type ManageTranslationsOpenProviderProps = {
	children: React.ReactNode;
	/**
	 * Initial item scope. When provided, the view opens scoped to this item id
	 * (equivalent to calling `open(itemId)`). Defaults to `null` (whole extension).
	 */
	defaultActiveItemId?: string | null;
	/** Initial open state. Defaults to `false`. */
	defaultIsOpen?: boolean;
};

/**
 * Provides the manage-translations open/close state to its subtree. Wrap a config
 * panel's content in this provider so the panel body and any nested trigger can share
 * the same open state.
 */
export const ManageTranslationsOpenProvider = ({
	children,
	defaultIsOpen = false,
	defaultActiveItemId = null,
}: ManageTranslationsOpenProviderProps): React.JSX.Element => {
	const [isOpen, setIsOpen] = useState(defaultIsOpen);
	const [activeItemId, setActiveItemId] = useState<string | null>(defaultActiveItemId);

	const value = useMemo<ManageTranslationsOpenContextValue>(
		() => ({
			isOpen,
			activeItemId,
			setIsOpen: (nextIsOpen: boolean) => {
				setIsOpen(nextIsOpen);
				// Clearing the open state also clears any item scope so a subsequent open
				// without an item id shows the whole extension again.
				if (!nextIsOpen) {
					setActiveItemId(null);
				}
			},
			open: (itemId?: string) => {
				setActiveItemId(itemId ?? null);
				setIsOpen(true);
			},
			close: () => {
				setActiveItemId(null);
				setIsOpen(false);
			},
		}),
		[isOpen, activeItemId],
	);

	return (
		<ManageTranslationsOpenContext.Provider value={value}>
			{children}
		</ManageTranslationsOpenContext.Provider>
	);
};

/**
 * Reads the manage-translations open/close context.
 *
 * @throws If used outside of a `ManageTranslationsOpenProvider`.
 */
export const useManageTranslationsOpen = (): ManageTranslationsOpenContextValue => {
	const context = useManageTranslationsOpenContext();

	if (context === null) {
		throw new Error(
			'useManageTranslationsOpen must be used within a ManageTranslationsOpenProvider',
		);
	}

	return context;
};

/**
 * Reads the manage-translations open/close context, returning `null` when used outside
 * of a `ManageTranslationsOpenProvider`. Prefer {@link useManageTranslationsOpen} for
 * required consumers; this variant is for components that may render either standalone
 * (controlled via props) or inside the provider.
 */
export const useManageTranslationsOpenContext = (): ManageTranslationsOpenContextValue | null =>
	useContext(ManageTranslationsOpenContext);
